'use client';

import { type ActivityType } from '@streetferret/api';
import { Button } from '@streetferret/ui-web/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@streetferret/ui-web/dialog';
import { NumberTooltip } from '@streetferret/ui-web/number-tooltip';
import { Separator } from '@streetferret/ui-web/separator';
import { cn, nFormatter } from '@streetferret/utils';
import { motion } from 'motion/react';
import Fuse from 'fuse.js';
import { Expand } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

export type BarDatum = {
  beforeBar?: () => ReactNode;
  afterBar?: () => ReactNode;
  title: string;
  value: number;
  highlight?: boolean;
};

interface BarsListProps {
  data: BarDatum[];
  total: number;
  title: string;
  limit?: number;
  activityType: ActivityType;
  unit: string;
}
export function BarsList({
  data,
  total,
  title,
  limit = 10,
  activityType,
  unit,
}: BarsListProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = () => setIsOpen(false);
  const openModal = () => setIsOpen(true);

  const valueFormatter = (number: number) => {
    return `${nFormatter(number)} ${unit}`;
  };
  return (
    <div className="relative overflow-hidden pt-4">
      <Bars
        limit={limit}
        data={data}
        valueFormatter={valueFormatter}
        total={total}
        activityType={activityType}
      />
      {data.length > limit ? (
        <div className="sticky inset-x-0 bottom-0 bg-gradient-to-t from-background to-transparent z-20">
          <div className="w-full -mt-2.5 bg-gradient-to-t from-background to-transparent flex items-center justify-center">
            <Button
              onClick={openModal}
              variant={'outline'}
              size={'sm'}
              type="button"
              className="bg-background">
              <Expand className="h-4 w-4 mr-1" />
              View All
            </Button>
          </div>
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
              </DialogHeader>
              <Separator />

              <div className="-mt-3.5">
                <div className="relative h-[450px] overflow-y-scroll overflow-x-hidden pt-2">
                  <Bars
                    data={data}
                    valueFormatter={valueFormatter}
                    total={total}
                    activityType={activityType}
                  />
                  <div className="sticky inset-x-0 bottom-0 bg-gradient-to-t from-background to-transparent h-20"></div>
                </div>
                <Button
                  className="w-full"
                  variant={'outline'}
                  onClick={closeModal}>
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div className="h-2"></div>
      )}
    </div>
  );
}

export function Bars({
  limit,
  data,
  total,
  valueFormatter,
  search,
  activityType,
}: {
  limit?: number;
  data: BarDatum[];
  valueFormatter: (number: number) => string;
  total: number;
  search?: string;
  activityType: ActivityType;
}) {
  const fuse = useMemo(() => {
    if (limit) {
      return null;
    }
    return new Fuse(data, {
      keys: ['title'],
    });
  }, [limit, data]);

  const filteredData = useMemo(() => {
    if (limit) {
      return data.slice(0, limit);
    } else {
      return search ? fuse!.search(search).map(r => r.item) : data;
    }
  }, [data, limit, search, fuse]);
  return (
    <div className="grid gap-2">
      {filteredData.map(
        ({ title, value, beforeBar, afterBar, highlight }, idx) => (
          <div key={idx} className="grid">
            <div className="w-10 tabular-nums">{beforeBar?.()}</div>
            <div className="relative z-10 flex items-center justify-between">
              <div className="h-8 !px-2 z-10 gap-2 flex items-center">
                {title}
              </div>
              <motion.div
                style={{
                  width: `${(value / (total || 0)) * 100}%`,
                }}
                className={cn(
                  'absolute h-8 origin-left rounded-md',
                  activityType === 'ride' && 'bg-ride/60',
                  activityType === 'run' && 'bg-run/60',
                  highlight && 'shadow-foreground/40 shadow',
                  activityType === 'run' && highlight && 'border-run/90',
                  activityType === 'ride' && highlight && 'border-ride/90',
                )}
                transition={{
                  ease: 'easeOut',
                  duration: 0.35 + idx * 0.01,
                  delay: 0.2 + idx * 0.04,
                }}
                initial={{ transform: 'scaleX(0)' }}
                animate={{ transform: 'scaleX(1)' }}
              />
              <NumberTooltip value={value}>
                <div className="text-right w-24 z-10 mr-2 tabular-nums font-bold">
                  {valueFormatter(value)}
                </div>
              </NumberTooltip>
            </div>
            <div className="text-center flex items-center justify-center">
              {afterBar?.()}
            </div>
          </div>
        ),
      )}
    </div>
  );
}
